import { SlideEngine as a } from "@slidejs/core";
class r extends Error {
  /**
   * @param message - 错误消息
   * @param code - 错误代码（可选）
   */
  constructor(e, t) {
    super(e), this.code = t, this.name = "SlideRunnerError", Object.setPrototypeOf(this, r.prototype);
  }
}
class l {
  /**
   * 创建 SlideRunner 实例
   *
   * @param config - SlideRunner 配置
   * @throws {SlideRunnerError} 如果配置无效
   */
  constructor(e) {
    if (this.slides = [], this.currentIndex = 0, this.isInitialized = !1, !e.adapter)
      throw new r("Adapter is required", "MISSING_ADAPTER");
    this.adapter = e.adapter, this.plugins = e.plugins ?? [], this.container = this.resolveContainer(e.container), this.adapterOptions = e.adapterOptions, this.setupAdapterEventListeners();
  }
  /**
   * 运行幻灯片演示
   *
   * 完整流程：
   * 1. 使用 SlideEngine 生成幻灯片
   * 2. 执行 beforeRender 插件钩子
   * 3. 初始化适配器
   * 4. 渲染幻灯片
   * 5. 执行 afterRender 插件钩子
   *
   * @param dsl - Slide DSL 对象
   * @param context - 幻灯片上下文数据
   */
  async run(e, t) {
    try {
      const i = new a(e);
      this.slides = i.generate(t), await this.executePluginHook("beforeRender", this.slides), this.isInitialized || (await this.adapter.initialize(this.container, this.adapterOptions), this.isInitialized = !0), await this.adapter.render(this.slides), await this.executePluginHook("afterRender", this.slides), this.currentIndex = 0;
    } catch (i) {
      const s = i instanceof Error ? i.message : String(i);
      throw new r(`Failed to run slides: ${s}`, "RUN_FAILED");
    }
  }
  /**
   * 直接渲染幻灯片数组
   *
   * 跳过 SlideEngine 生成步骤，直接渲染提供的幻灯片
   *
   * @param slides - 幻灯片定义数组
   */
  async renderSlides(e) {
    try {
      this.slides = e, await this.executePluginHook("beforeRender", this.slides), this.isInitialized || (await this.adapter.initialize(this.container, this.adapterOptions), this.isInitialized = !0), await this.adapter.render(this.slides), await this.executePluginHook("afterRender", this.slides), this.currentIndex = 0;
    } catch (t) {
      const i = t instanceof Error ? t.message : String(t);
      throw new r(`Failed to render slides: ${i}`, "RENDER_FAILED");
    }
  }
  /**
   * 导航到指定幻灯片
   *
   * 执行 beforeSlideChange 和 afterSlideChange 插件钩子
   *
   * @param index - 目标幻灯片索引
   * @throws {SlideRunnerError} 如果索引超出范围
   */
  navigateTo(e) {
    if (e < 0 || e >= this.slides.length)
      throw new r(
        `Invalid slide index: ${e}. Valid range: 0-${this.slides.length - 1}`,
        "INVALID_INDEX"
      );
    const t = this.currentIndex, i = e;
    this.executePluginHook("beforeSlideChange", t, i).then(() => {
      this.adapter.navigateTo(e), this.currentIndex = e, this.executePluginHook("afterSlideChange", t, i).catch((s) => {
        console.error("Plugin afterSlideChange hook failed:", s);
      });
    });
  }
  /**
   * 播放/启动演示文稿
   *
   * 导航到第一张幻灯片（索引 0），开始演示
   */
  play() {
    if (this.slides.length === 0)
      throw new r(
        "No slides to play. Call run() or renderSlides() first.",
        "NO_SLIDES"
      );
    if (!this.isInitialized)
      throw new r(
        "Adapter not initialized. Call run() or renderSlides() first.",
        "NOT_INITIALIZED"
      );
    this.navigateTo(0);
  }
  /**
   * 获取当前幻灯片索引
   */
  getCurrentIndex() {
    return this.adapter.getCurrentIndex();
  }
  /**
   * 获取幻灯片总数
   */
  getTotalSlides() {
    return this.adapter.getTotalSlides();
  }
  /**
   * 更新指定幻灯片
   *
   * 仅当适配器支持 updateSlide 方法时可用
   *
   * @param index - 幻灯片索引
   * @param slide - 新的幻灯片定义
   * @throws {SlideRunnerError} 如果适配器不支持更新
   */
  async updateSlide(e, t) {
    if (!this.adapter.updateSlide)
      throw new r(
        `Adapter "${this.adapter.name}" does not support updateSlide`,
        "UNSUPPORTED_OPERATION"
      );
    if (e < 0 || e >= this.slides.length)
      throw new r(
        `Invalid slide index: ${e}. Valid range: 0-${this.slides.length - 1}`,
        "INVALID_INDEX"
      );
    try {
      this.slides[e] = t, await this.adapter.updateSlide(e, t);
    } catch (i) {
      const s = i instanceof Error ? i.message : String(i);
      throw new r(`Failed to update slide: ${s}`, "UPDATE_FAILED");
    }
  }
  /**
   * 刷新当前幻灯片
   *
   * 重新渲染所有幻灯片
   */
  async refresh() {
    try {
      await this.adapter.render(this.slides);
    } catch (e) {
      const t = e instanceof Error ? e.message : String(e);
      throw new r(`Failed to refresh slides: ${t}`, "REFRESH_FAILED");
    }
  }
  /**
   * 销毁 SlideRunner，清理资源
   */
  async destroy() {
    try {
      await this.adapter.destroy(), this.slides = [], this.currentIndex = 0, this.isInitialized = !1;
    } catch (e) {
      const t = e instanceof Error ? e.message : String(e);
      throw new r(`Failed to destroy runner: ${t}`, "DESTROY_FAILED");
    }
  }
  /**
   * 注册事件监听器
   *
   * @param event - 事件类型
   * @param handler - 事件处理器
   */
  on(e, t) {
    this.adapter.on(e, t);
  }
  /**
   * 移除事件监听器
   *
   * @param event - 事件类型
   * @param handler - 事件处理器
   */
  off(e, t) {
    this.adapter.off(e, t);
  }
  /**
   * 解析容器元素
   *
   * @param container - 容器元素或选择器
   * @returns 解析后的 HTMLElement
   * @throws {SlideRunnerError} 如果容器无效或找不到
   */
  resolveContainer(e) {
    if (typeof e == "string") {
      const t = document.querySelector(e);
      if (!t)
        throw new r(
          `Container element not found: ${e}`,
          "CONTAINER_NOT_FOUND"
        );
      return t;
    }
    if (!(e instanceof HTMLElement))
      throw new r(
        "Container must be an HTMLElement or a valid selector",
        "INVALID_CONTAINER"
      );
    return e;
  }
  /**
   * 执行插件钩子
   *
   * @param hookName - 钩子名称
   * @param args - 钩子参数
   */
  async executePluginHook(e, ...t) {
    for (const i of this.plugins) {
      const s = i[e];
      if (typeof s == "function")
        try {
          await s.apply(i, t);
        } catch (n) {
          console.error(`Plugin "${i.name}" ${String(e)} hook failed:`, n);
        }
    }
  }
  /**
   * 设置适配器事件监听
   */
  setupAdapterEventListeners() {
    this.adapter.on("slideChanged", (e) => {
      typeof e == "object" && e !== null && "index" in e && (this.currentIndex = e.index);
    });
  }
}
export {
  l as SlideRunner,
  r as SlideRunnerError
};
//# sourceMappingURL=index.mjs.map
